/**
 * MotivoBajaDialog — pide el motivo antes de dar de baja a un cliente (ORR-404).
 *
 * Hasta acá "Dar de Baja" era un click directo desde la tabla. Este diálogo interpone
 * el paso y es el único camino: sin motivo elegido no se confirma.
 */
import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FormDialogContent } from '@/components/ui/form-dialog-content'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'

import { MOTIVOS_BAJA, type MotivoBajaFormValues } from './motivoBajaSchema'
import { useMotivoBajaForm } from './useMotivoBajaForm'

interface MotivoBajaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Nombre del cliente que se da de baja, para que nadie confunda la fila. */
  nombreCliente?: string | null
  /** Devuelve `false` si la baja falló: el diálogo queda abierto para reintentar. */
  onConfirmar: (values: MotivoBajaFormValues) => boolean | Promise<boolean>
}

export function MotivoBajaDialog({
  open,
  onOpenChange,
  nombreCliente,
  onConfirmar,
}: MotivoBajaDialogProps) {
  const { form } = useMotivoBajaForm({
    onSubmit: async (values) => {
      // "Otros" es el único motivo con detalle. Normalizar acá y no en cada caller: el
      // diálogo es quien conoce la regla, y así nadie la reimplementa al reusarlo.
      const ok = await onConfirmar({
        motivo: values.motivo,
        detalle: values.motivo === 'otros' ? values.detalle?.trim() || null : null,
      })
      if (!ok) return
      form.reset()
      onOpenChange(false)
    },
  })

  // Leído del store y no de `form.state`: este último no re-renderiza, así que la guarda
  // de descarte y el textarea condicional mirarían un valor viejo.
  const isDirty = useStore(form.store, (s) => s.isDirty)
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting)
  const motivo = useStore(form.store, (s) => s.values.motivo)

  // La baja ya está en vuelo: cerrar acá haría desaparecer el diálogo sin cancelar el
  // request, y el cliente quedaría dado de baja igual. El botón X y el click afuera
  // llegan por acá, no solo Cancelar.
  const cerrar = () => {
    if (isSubmitting) return
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : cerrar())}>
      <FormDialogContent isDirty={isDirty} onDiscard={cerrar} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dar de baja el cliente</DialogTitle>
          <DialogDescription>
            {nombreCliente
              ? `Elegí por qué se da de baja a ${nombreCliente}.`
              : 'Elegí por qué se da de baja al cliente.'}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="motivo">
            {(field) => (
              <div className="space-y-2">
                <RadioGroup
                  value={field.state.value || ''}
                  onValueChange={(value) =>
                    field.handleChange(value as MotivoBajaFormValues['motivo'])
                  }
                  aria-label="Motivo de la baja"
                >
                  {MOTIVOS_BAJA.map((m) => (
                    <div key={m.value} className="flex items-center gap-2">
                      <RadioGroupItem value={m.value} id={`motivo-${m.value}`} />
                      <Label htmlFor={`motivo-${m.value}`} className="font-normal cursor-pointer">
                        {m.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-destructive text-sm">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {motivo === 'otros' && (
            <form.Field name="detalle">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="motivo-baja-detalle">¿Cuál fue el motivo?</Label>
                  <Textarea
                    id="motivo-baja-detalle"
                    value={field.state.value || ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    rows={3}
                    placeholder="Contá brevemente qué pasó"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Dar de baja
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
